import {Box, Button, Checkbox, FormControlLabel, Grid, Typography} from '@mui/material';
import {useCan} from 'src/permissions/hooks';
import {ChangeEvent, FC, useEffect, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import SignatureCanvas from 'react-signature-canvas';
import {NotifyWarning} from 'src/utils/notification';
import {CancelButton} from '../CancelButton';
import {SubmitButton} from '../SubmitButton';
import {actionsActiveButtonsSx, actionsWrapperSx, canvasWrapperSx, headerSx, rootSx, titleSx,} from './Signature.sx';
import {SignatureTypes} from './Signature.types';
import {TermsText} from '../TermsText';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import {usersApi} from 'src/api';
import {setAuthInfo} from 'src/stores/common/commonSlice';

export const Signature: FC<SignatureTypes> = ({
                                                  modal = false,
                                                  handleSubmit,
                                                  handleClose,
                                                  signature = '',
                                                  canvasHeight,
                                              }) => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const canvas = useRef<SignatureCanvas>(null);
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const canReadTermsOfContract = useCan('businessTerms.read');
    const {authInfo} = useAppSelector((state) => state.common);
    const [getDriver] = usersApi.useLazyGetUserByIdQuery();

    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [agreeWithTerms, setAgreeWithTerms] = useState(false);

    const handleAgreeTerms = (event: ChangeEvent<HTMLInputElement>) => {
        setAgreeWithTerms(event.target.checked);
    };

    const handleSubmitSigning = async () => {
        if (!canvas.current) {
            return;
        }
        if (canvas.current.isEmpty()) {
            NotifyWarning(intl.formatMessage({id: 'fillSignatureField'}));
            return;
        }
        setDisableButton(true);
        fetch(canvas.current.toDataURL('png'))
            .then(function (res) {
                return res.arrayBuffer();
            })
            .then(function (buf) {
                handleSubmit(new File([buf], 'signature.txt'));
            });

        const isDriver = authInfo.persona.isDriver;
        if (isDriver) {
            try {
                const response = await getDriver({
                    userId: authInfo.id,
                    businessId: businessId,
                }).unwrap();

                dispatch(setAuthInfo(response));
            } catch (e) {
                console.error('COULD NOT GET CURRENT DRIVER', e);
            }
        }
        setTimeout(() => {
            setDisableButton(false);
        }, 2000);
    };

    useEffect(() => {
        signature && canvas.current?.fromDataURL(signature);
    }, [signature]);

    const handleClear = () => {
        if (!canvas.current) {
            return;
        }

        canvas.current.clear();
    };
    return (
        <Box sx={rootSx(modal)}>
            <Grid
                container
                spacing={2}
                sx={titleSx(modal)}
            >
                {!modal && (
                    <Grid
                        item
                        xs={12}
                        sx={headerSx}
                    >
                        <Typography variant="subtitle1">
                            <FormattedMessage id="putSignature"/>
                        </Typography>
                        <Button onClick={handleClear}>
                            <Typography
                                variant="button"
                                color="primary.main"
                            >
                                <FormattedMessage id="clear"/>
                            </Typography>
                        </Button>
                    </Grid>
                )}
                {canReadTermsOfContract && (
                    <Grid
                        item
                        xs={12}
                        sx={{
                            ml: modal ? 2 : 0,
                            mt: modal ? 2 : 0,
                        }}
                    >
                        <FormControlLabel
                            label={<TermsText/>}
                            control={
                                <Checkbox
                                    color="secondary"
                                    name="agreements"
                                    value={agreeWithTerms}
                                    onChange={handleAgreeTerms}
                                    required
                                />
                            }
                        />
                    </Grid>
                )}
                <Grid
                    item
                    xs={12}
                    sx={canvasWrapperSx(modal, canvasHeight)}
                >
                    <SignatureCanvas
                        ref={canvas}
                        canvasProps={{className: 'sigCanvas'}}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                >
                    {!modal && (
                        <SubmitButton
                            text="sign"
                            color="brand"
                            fullWidth
                            handler={handleSubmitSigning}
                            isSubmitting={disableButton || (!agreeWithTerms && canReadTermsOfContract)}
                        />
                    )}
                    {modal && (
                        <Box sx={actionsWrapperSx}>
                            {handleClose && (
                                <CancelButton
                                    text="cancel"
                                    handler={handleClose}
                                />
                            )}
                            <Box sx={actionsActiveButtonsSx}>
                                <Button onClick={handleClear}>
                                    <Typography
                                        variant="button"
                                        color="primary.main"
                                    >
                                        <FormattedMessage id="clear"/>
                                    </Typography>
                                </Button>
                                <SubmitButton
                                    text="submit"
                                    color="primary"
                                    handler={handleSubmitSigning}
                                    isSubmitting={disableButton || (!agreeWithTerms && canReadTermsOfContract)}
                                />
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};
